import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { getBlogPosts, getBlogCategories, useQuery } from "wasp/client/operations";
import BlogCard from "./components/BlogCard";
import { Button } from "../client/components/ui/button";
import useTranslation from "../client/hooks/useTranslation";

const PAGE_SIZE = 12;

export default function BlogListPage() {
  const t = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [skip, setSkip] = useState(0);
  const [allPosts, setAllPosts] = useState<any[]>([]);

  const { data: categoriesData } = useQuery(getBlogCategories, undefined);
  const { data: postsData, isLoading } = useQuery(getBlogPosts, {
    categorySlug: selectedCategory,
    take: PAGE_SIZE,
    skip,
  });

  useEffect(() => {
    if (postsData && !isLoading) {
      if (skip === 0) {
        setAllPosts(postsData.posts);
      } else {
        setAllPosts((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const newPosts = postsData.posts.filter((p: any) => !existingIds.has(p.id));
          return [...prev, ...newPosts];
        });
      }
    }
  }, [postsData, isLoading, skip]);

  const handleCategoryChange = (slug: string | undefined) => {
    setSelectedCategory(slug);
    setSkip(0);
    setAllPosts([]);
  };

  const hasMore = postsData ? skip + PAGE_SIZE < postsData.total : false;

  return (
    <>
      <Helmet>
        <title>{t.blog.title} — Edgely</title>
        <meta name="description" content={t.blog.subtitle} />
        <meta property="og:title" content={`${t.blog.title} — Edgely`} />
        <meta property="og:description" content={t.blog.subtitle} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://edgely.trade/blog" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <h1 className="text-4xl font-bold text-foreground mb-2">{t.blog.title}</h1>
          <p className="text-muted-foreground mb-8">{t.blog.subtitle}</p>

          {categoriesData && categoriesData.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              <button
                onClick={() => handleCategoryChange(undefined)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  !selectedCategory
                    ? "bg-primary text-primary-foreground"
                    : "bg-card text-muted-foreground hover:text-foreground border border-border"
                }`}
              >
                {t.blog.allCategories}
              </button>
              {categoriesData.map((cat: any) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === cat.slug
                      ? "bg-primary text-primary-foreground"
                      : "bg-card text-muted-foreground hover:text-foreground border border-border"
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          )}

          {isLoading && allPosts.length === 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-72 rounded-xl bg-card animate-pulse" />
              ))}
            </div>
          ) : allPosts.length === 0 ? (
            <p className="text-muted-foreground">{t.blog.noPostsFound}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {allPosts.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          )}

          {hasMore && (
            <div className="flex justify-center mt-10">
              <Button
                variant="outline"
                onClick={() => setSkip((s) => s + PAGE_SIZE)}
                disabled={isLoading}
              >
                {t.blog.loadMore}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
